import { useEffect, useState } from "react";
import { api, API, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CurrencyDollar, CheckCircle, Warning, FilePdf, PencilSimple, Sparkle } from "@phosphor-icons/react";
import PhotoOpen from "@/components/PhotoOpen";

// Shared panel showing all tenant-chargeable damages on a Turn job.
// - Field workers see a read-only list (summary + descriptions).
// - Admins can edit deduction amount, office notes, mark reviewed, and export the court-ready PDF.
export default function ChargebackPanel({ jobId, isAdmin, onJobReload }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState({}); // { file_id: {amount, notes, reviewed} }
  const [busy, setBusy] = useState(null);
  const [aiBusy, setAiBusy] = useState(null);
  const [classifyBusy, setClassifyBusy] = useState(null);
  const [classifierEnabled, setClassifierEnabled] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/settings").then(r => setClassifierEnabled(!!r.data?.ai_damage_classifier_enabled)).catch(() => {});
  }, [isAdmin]);

  const load = () =>
    api.get(`/jobs/${jobId}/tenant-chargeback`).then((r) => {
      setData(r.data);
      const init = {};
      for (const it of r.data.items || []) {
        init[it.file_id] = {
          amount: it.office_deduction_amount ? String(it.office_deduction_amount) : "",
          notes: it.office_notes || "",
          reviewed: it.office_reviewed,
        };
      }
      setEditing(init);
    });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [jobId]);

  if (!data) return <div className="mt-5 p-4 text-sm font-bold text-slate-500">Loading chargeback…</div>;

  const saveItem = async (fid) => {
    setBusy(fid);
    try {
      await api.patch(`/jobs/${jobId}/photo/${fid}/chargeable`, {
        office_deduction_amount: parseFloat(editing[fid]?.amount) || 0,
        office_notes: (editing[fid]?.notes || "").trim(),
        office_reviewed: !!editing[fid]?.reviewed,
      });
      toast.success("Saved");
      await load();
      if (onJobReload) await onJobReload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally { setBusy(null); }
  };

  const clearFlag = async (fid) => {
    if (!window.confirm("Remove tenant-chargeable flag from this photo?")) return;
    try {
      await api.patch(`/jobs/${jobId}/photo/${fid}/chargeable`, { chargeable_to_tenant: false });
      toast.success("Flag cleared");
      await load();
      if (onJobReload) await onJobReload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const aiEstimate = async (it) => {
    setAiBusy(it.file_id);
    try {
      const r = await api.post(`/jobs/${jobId}/photo/${it.file_id}/ai-estimate`);
      const est = r.data;
      // Auto-fill deduction with midpoint and append breakdown to notes
      const midpoint = est.midpoint || ((est.low + est.high) / 2) || 0;
      const range = `AI: $${(est.low || 0).toFixed(0)}–$${(est.high || 0).toFixed(0)} · ${est.confidence || "med"} confidence`;
      const breakdown = est.breakdown ? `\n${est.breakdown}` : "";
      const wearNote = est.is_wear_and_tear ? "\n⚠️ AI flagged as normal wear-and-tear — may not be chargeable." : "";
      const existingNotes = (editing[it.file_id]?.notes || "").trim();
      const combined = [existingNotes, `${range}${breakdown}${wearNote}`].filter(Boolean).join("\n\n");
      setEditing({
        ...editing,
        [it.file_id]: {
          ...editing[it.file_id],
          amount: midpoint ? String(midpoint.toFixed(2)) : (editing[it.file_id]?.amount || ""),
          notes: combined,
        },
      });
      if (est.is_wear_and_tear) {
        toast.warning("AI flagged this as normal wear-and-tear");
      } else {
        toast.success(`AI estimate: $${est.low.toFixed(0)}–$${est.high.toFixed(0)}`);
      }
      return est;
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || "AI estimate failed";
      toast.error(detail);
      return null;
    } finally { setAiBusy(null); }
  };

  const classifyDamage = async (it) => {
    setClassifyBusy(it.file_id);
    try {
      const r = await api.post(`/jobs/${jobId}/photo/${it.file_id}/classify-damage`);
      const c = r.data;
      const label = (c.classification || "").replace("_", " ");
      const chargeableLine = c.chargeable
        ? `AI Classifier: ${label} (${c.severity}) — CHARGEABLE, ${c.confidence} confidence`
        : `AI Classifier: ${label} (${c.severity}) — not chargeable, ${c.confidence} confidence`;
      const reasoningLine = c.reasoning ? `\n${c.reasoning}` : "";
      const rangeLine = (c.suggested_low > 0 || c.suggested_high > 0)
        ? `\nSuggested chargeback: $${(c.suggested_low || 0).toFixed(0)}–$${(c.suggested_high || 0).toFixed(0)}`
        : "";
      const midpoint = c.chargeable
        ? (((c.suggested_low || 0) + (c.suggested_high || 0)) / 2)
        : 0;
      // Load fresh chargeback items FIRST so ai_classification chip renders,
      // THEN merge classifier-derived amount/notes so they aren't overwritten.
      await load();
      setEditing(prev => {
        const existingNotes = (prev[it.file_id]?.notes || "").trim();
        const combined = [existingNotes, `${chargeableLine}${reasoningLine}${rangeLine}`].filter(Boolean).join("\n\n");
        return {
          ...prev,
          [it.file_id]: {
            ...prev[it.file_id],
            amount: (midpoint > 0) ? String(midpoint.toFixed(2)) : (prev[it.file_id]?.amount || ""),
            notes: combined,
          },
        };
      });
      if (c.classification === "tenant_damage") {
        toast.success(`AI classified: TENANT DAMAGE (${c.severity})`);
      } else if (c.classification === "wear_and_tear") {
        toast.warning("AI classified: normal wear-and-tear");
      } else {
        toast.info(`AI classified: ${label}`);
      }
      return c;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI classify failed");
      return null;
    } finally { setClassifyBusy(null); }
  };

  const bulkAiEstimate = async () => {
    const targets = (data?.items || []).filter(i => !i.office_reviewed && !i.ai_estimate);
    if (targets.length === 0) {
      toast.info("Nothing to estimate — all unreviewed items already have AI estimates");
      return;
    }
    if (!window.confirm(`Run AI estimate on ${targets.length} unreviewed damage${targets.length === 1 ? "" : "s"}? This may take up to ${targets.length * 8}s.`)) return;
    setBulkBusy(true);
    let ok = 0, fail = 0, wear = 0;
    // Serial to avoid rate limiting
    for (const it of targets) {
      try {
        const r = await api.post(`/jobs/${jobId}/photo/${it.file_id}/ai-estimate`);
        const est = r.data;
        if (est.is_wear_and_tear) wear += 1;
        const midpoint = est.midpoint || ((est.low + est.high) / 2) || 0;
        setEditing(prev => ({
          ...prev,
          [it.file_id]: {
            ...prev[it.file_id],
            amount: midpoint ? String(midpoint.toFixed(2)) : (prev[it.file_id]?.amount || ""),
            notes: [
              (prev[it.file_id]?.notes || "").trim(),
              `AI: $${(est.low || 0).toFixed(0)}–$${(est.high || 0).toFixed(0)} · ${est.confidence || "med"} confidence${est.breakdown ? "\n" + est.breakdown : ""}${est.is_wear_and_tear ? "\n⚠️ Flagged as wear-and-tear" : ""}`
            ].filter(Boolean).join("\n\n"),
          },
        }));
        ok += 1;
      } catch (_) { fail += 1; }
    }
    await load();
    if (onJobReload) await onJobReload();
    setBulkBusy(false);
    toast.success(`AI ran on ${ok} item${ok === 1 ? "" : "s"}${fail ? ` · ${fail} failed` : ""}${wear ? ` · ${wear} flagged as wear-and-tear` : ""}`);
  };

  const s = data.summary || {};
  const pdfHref = `${API}/jobs/${jobId}/tenant-chargeback-pdf?auth=${encodeURIComponent(localStorage.getItem("access_token") || "")}`;

  return (
    <div className="mt-5 space-y-5" data-testid="chargeback-panel">
      {/* Header */}
      <div className="rugged-card p-4 border-l-4 border-red-600 bg-red-50">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-display font-black text-xl uppercase text-red-700 flex items-center gap-2">
              <CurrencyDollar weight="bold" size={22}/> Tenant Chargeback
            </h3>
            <p className="text-xs text-slate-600 font-bold uppercase tracking-wider mt-0.5">
              Move-out damages flagged as tenant-responsible
            </p>
          </div>
          {s.total_flagged > 0 && (
            <a href={pdfHref} target="_blank" rel="noreferrer" data-testid="chargeback-pdf-btn"
              className="tactile-btn h-11 px-4 bg-red-600 hover:bg-red-700 text-white font-black uppercase text-sm tracking-wider inline-flex items-center gap-2">
              <FilePdf weight="fill" size={16}/> Evidence Report
            </a>
          )}
        </div>
        <div className="mt-3 grid grid-cols-3 gap-3">
          <StatBox label="Flagged" value={s.total_flagged || 0} testid="cb-stat-flagged"/>
          <StatBox label="Reviewed" value={s.total_reviewed || 0} testid="cb-stat-reviewed"
            accent={s.pending_review > 0 ? "text-amber-600" : "text-green-700"}/>
          <StatBox label="Total $" value={`$${(s.total_amount || 0).toFixed(2)}`} testid="cb-stat-amount"
            accent="text-red-700"/>
        </div>
      </div>

      {/* Empty state */}
      {(data.items || []).length === 0 && (
        <div className="rugged-card p-6 text-center bg-slate-50" data-testid="chargeback-empty">
          <Warning weight="bold" size={32} className="mx-auto text-slate-400"/>
          <div className="mt-2 font-black uppercase text-sm tracking-wider text-slate-500">
            No tenant damages flagged yet
          </div>
          <p className="mt-1 text-xs text-slate-500">
            On the <b>Work</b> tab, open any photo and tap <b>$ Flag</b> to mark tenant-responsible damage.
          </p>
        </div>
      )}

      {/* Items */}
      {(data.items || []).map((it) => (
        <div key={it.file_id} data-testid={`cb-item-${it.file_id}`}
          className={`rugged-card p-4 border-l-4 ${it.office_reviewed ? "border-green-600" : "border-amber-500"}`}>
          <div className="flex flex-col sm:flex-row gap-4">
            <PhotoOpen photo={it}
              className="block w-full sm:w-40 h-40 border-2 border-[#0f172a] bg-slate-100 flex-shrink-0 overflow-hidden">
              {it.media_type === "video" ? (
                <video src={fileUrl(it.path)} className="w-full h-full object-cover" muted/>
              ) : (
                <img src={fileUrl(it.path)} alt="" className="w-full h-full object-cover"/>
              )}
            </PhotoOpen>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {it.office_reviewed ? (
                  <span className="px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-white bg-green-600 inline-flex items-center gap-1">
                    <CheckCircle weight="fill" size={12}/> Reviewed
                  </span>
                ) : (
                  <span className="px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-white bg-amber-500 inline-flex items-center gap-1">
                    <Warning weight="fill" size={12}/> Pending Office Review
                  </span>
                )}
                {(it.tags || []).map(tg => (
                  <span key={tg} className="px-1.5 py-0.5 text-[9px] font-black uppercase bg-[#0f172a] text-white">{tg}</span>
                ))}
              </div>
              <p className="mt-2 text-sm text-slate-800 whitespace-pre-wrap font-medium">
                {it.damage_description || <span className="italic text-slate-400">No description provided</span>}
              </p>
              <div className="mt-1 text-[11px] text-slate-500">
                Flagged by <b>{it.flagged_by_name || "—"}</b>
                {it.flagged_at && ` · ${new Date(it.flagged_at).toLocaleString()}`}
              </div>
              {it.office_reviewed && it.reviewed_by_name && (
                <div className="mt-0.5 text-[11px] text-slate-500">
                  Reviewed by <b>{it.reviewed_by_name}</b>
                  {it.reviewed_at && ` · ${new Date(it.reviewed_at).toLocaleString()}`}
                </div>
              )}

              {isAdmin ? (
                <div className="mt-3 space-y-2 border-t border-dashed border-slate-300 pt-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Deduction $</label>
                      <input type="number" step="0.01" min="0"
                        value={editing[it.file_id]?.amount || ""}
                        onChange={e => setEditing({ ...editing, [it.file_id]: { ...editing[it.file_id], amount: e.target.value }})}
                        data-testid={`cb-amount-${it.file_id}`}
                        className="mt-0.5 w-full h-9 px-2 border-2 border-[#0f172a] font-mono text-sm"/>
                    </div>
                    <label className="flex items-center gap-2 mt-5 cursor-pointer" data-testid={`cb-reviewed-${it.file_id}`}>
                      <input type="checkbox"
                        checked={!!editing[it.file_id]?.reviewed}
                        onChange={e => setEditing({ ...editing, [it.file_id]: { ...editing[it.file_id], reviewed: e.target.checked }})}
                        className="h-4 w-4"/>
                      <span className="text-xs font-black uppercase tracking-wider">Mark Reviewed</span>
                    </label>
                  </div>
                  <button onClick={() => aiEstimate(it)} disabled={aiBusy === it.file_id}
                    data-testid={`cb-ai-est-${it.file_id}`}
                    className="w-full h-9 border-2 border-violet-600 bg-violet-50 hover:bg-violet-100 text-violet-800 font-black uppercase text-xs tracking-wider inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
                    <Sparkle weight="fill" size={14}/>
                    {aiBusy === it.file_id ? "Analyzing…" : (it.ai_estimate ? "Re-run AI Estimate" : "AI Estimate Repair Cost")}
                  </button>
                  {classifierEnabled && (
                    <button onClick={() => classifyDamage(it)} disabled={classifyBusy === it.file_id}
                      data-testid={`cb-ai-classify-${it.file_id}`}
                      className="w-full h-9 border-2 border-emerald-600 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-black uppercase text-xs tracking-wider inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
                      <Sparkle weight="fill" size={14}/>
                      {classifyBusy === it.file_id ? "Classifying…" : (it.ai_classification ? "Re-run AI Classify" : "AI Classify Damage")}
                    </button>
                  )}
                  {it.ai_classification && (
                    <div className={`p-2 border-l-4 text-[11px] ${
                      it.ai_classification.classification === "tenant_damage" ? "bg-red-50 border-red-600" :
                      it.ai_classification.classification === "wear_and_tear" ? "bg-amber-50 border-amber-500" :
                      "bg-slate-50 border-slate-400"
                    }`} data-testid={`cb-classification-chip-${it.file_id}`}>
                      <div className="font-black uppercase flex items-center gap-1 flex-wrap">
                        <Sparkle weight="fill" size={11}/>
                        {(it.ai_classification.classification || "").replace("_", " ")}
                        <span className="ml-1 text-[9px] font-black uppercase px-1 py-0.5 bg-[#0f172a] text-white">{it.ai_classification.severity}</span>
                        <span className="ml-1 text-[9px] font-black uppercase px-1 py-0.5 bg-slate-500 text-white">{it.ai_classification.confidence} confidence</span>
                        {it.ai_classification.chargeable ? (
                          <span className="ml-1 text-[9px] font-black uppercase px-1 py-0.5 bg-red-600 text-white">Chargeable</span>
                        ) : (
                          <span className="ml-1 text-[9px] font-black uppercase px-1 py-0.5 bg-emerald-600 text-white">Not Chargeable</span>
                        )}
                        {(it.ai_classification.suggested_low > 0 || it.ai_classification.suggested_high > 0) && (
                          <span className="ml-1 text-[10px] font-black text-slate-800">
                            ${(it.ai_classification.suggested_low || 0).toFixed(0)}–${(it.ai_classification.suggested_high || 0).toFixed(0)}
                          </span>
                        )}
                      </div>
                      {it.ai_classification.damage_type && <div className="mt-1 text-slate-700"><b>Type:</b> {it.ai_classification.damage_type}</div>}
                      {it.ai_classification.reasoning && <div className="mt-1 text-slate-700">{it.ai_classification.reasoning}</div>}
                    </div>
                  )}
                  {it.ai_estimate && (
                    <div className="p-2 bg-violet-50 border-l-4 border-violet-600 text-[11px]" data-testid={`cb-ai-chip-${it.file_id}`}>
                      <div className="font-black uppercase text-violet-800 flex items-center gap-1">
                        <Sparkle weight="fill" size={11}/>
                        AI: ${(it.ai_estimate.low || 0).toFixed(0)}–${(it.ai_estimate.high || 0).toFixed(0)}
                        <span className="ml-1 text-[9px] font-black uppercase px-1 py-0.5 bg-violet-600 text-white">{it.ai_estimate.confidence || "med"}</span>
                        {it.ai_estimate.is_wear_and_tear && (
                          <span className="ml-1 text-[9px] font-black uppercase px-1 py-0.5 bg-amber-500 text-white">Wear &amp; Tear</span>
                        )}
                      </div>
                      {it.ai_estimate.breakdown && <div className="mt-1 text-slate-700">{it.ai_estimate.breakdown}</div>}
                    </div>
                  )}
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Office Notes</label>
                    <textarea rows={2}
                      value={editing[it.file_id]?.notes || ""}
                      onChange={e => setEditing({ ...editing, [it.file_id]: { ...editing[it.file_id], notes: e.target.value }})}
                      data-testid={`cb-notes-${it.file_id}`}
                      placeholder="Repair quote source, estimate breakdown…"
                      className="mt-0.5 w-full border-2 border-[#0f172a] p-2 text-xs"/>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button onClick={() => saveItem(it.file_id)}
                      disabled={busy === it.file_id}
                      data-testid={`cb-save-${it.file_id}`}
                      className="tactile-btn h-9 px-4 bg-[#0f172a] text-white font-black uppercase text-xs tracking-wider inline-flex items-center gap-1">
                      <PencilSimple weight="bold" size={14}/> {busy === it.file_id ? "Saving…" : "Save"}
                    </button>
                    <button onClick={() => clearFlag(it.file_id)}
                      data-testid={`cb-clear-${it.file_id}`}
                      className="h-9 px-3 border-2 border-red-500 text-red-600 font-black uppercase text-xs tracking-wider hover:bg-red-500 hover:text-white transition-colors">
                      Remove Flag
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                  <div className="p-2 bg-slate-50 border border-slate-200">
                    <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">Deduction</div>
                    <div className="mt-0.5 font-black text-red-700">${(it.office_deduction_amount || 0).toFixed(2)}</div>
                  </div>
                  <div className="p-2 bg-slate-50 border border-slate-200">
                    <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">Office Notes</div>
                    <div className="mt-0.5 text-slate-700">{it.office_notes || <span className="italic text-slate-400">—</span>}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function StatBox({ label, value, testid, accent = "text-[#0f172a]" }) {
  return (
    <div className="border-2 border-[#0f172a] bg-white p-2 text-center" data-testid={testid}>
      <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-0.5 font-display font-black text-xl ${accent}`}>{value}</div>
    </div>
  );
}
